import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Send, ShieldCheck, ArrowLeft, Trash2, RotateCcw, Loader2, Bot, User } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const EXAMPLES = [
  "Don't flag standalone swearing unless it's directed at a student.",
  'Ignore minor teaching errors at severity 3 or below.',
  'Always show poaching attempts even at low severity.',
  'Hide casual conversation flagged as inappropriate when it stays educational.',
];

const IssueGuardrails = ({ embedded = false } = {}) => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      const { data } = await supabase
        .from('organizations')
        .select('issue_filter_policy')
        .eq('id', organizationId)
        .single();
      setPolicy(data?.issue_filter_policy || {
        summary: 'Default TutorTracer filtering: show all flagged issues.',
        instructions: '', suppressions: [], version: 1,
      });
      setLoading(false);
    })();
  }, [organizationId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setInput('');
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('issue-filter-assistant', {
        body: { organization_id: organizationId, instruction: msg },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // The function keeps the stored policy untouched when a request can't be
      // turned into a rule. Report that as a failure, never as a silent success.
      if (data?.applied === false) {
        if (data.policy) setPolicy(data.policy);
        setMessages(m => [...m, {
          role: 'assistant',
          error: true,
          content: data.message || "I couldn't turn that into a suppression rule, so nothing was changed.",
        }]);
        toast.error('No rule created — guardrails unchanged');
        setSending(false);
        return;
      }

      setPolicy(data.policy);
      const mp = data.match_preview;
      const fallback = `Policy updated to v${data.policy?.version}. ${data.policy?.suppressions?.length || 0} suppression rule(s) active.`
        + (mp ? `\n\nImpact: ${mp.would_suppress} of ${mp.total_open} currently open issues are now hidden.` : '');
      setMessages(m => [...m, {
        role: 'assistant',
        content: `${data.message || fallback}\n\nActive policy: ${data.policy?.summary || ''}`.trim(),
      }]);
      toast.success(mp ? `Guardrails updated — ${mp.would_suppress} issues now hidden` : 'Guardrails updated');
    } catch (e) {
      console.error(e);
      setMessages(m => [...m, { role: 'assistant', content: `Couldn't update guardrails: ${e.message || e}. Has the issue-filter-assistant edge function been deployed?`, error: true }]);
    }
    setSending(false);
  };

  const removeRule = async (index) => {
    try {
      const { data, error } = await supabase.functions.invoke('issue-filter-assistant', {
        body: { organization_id: organizationId, action: 'delete_rule', index },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPolicy(data.policy);
      const mp = data.match_preview;
      setMessages(m => [...m, {
        role: 'assistant',
        content: `${data.message || 'Rule removed.'}${mp ? `\n\nImpact: ${mp.would_suppress} of ${mp.total_open} currently open issues are now hidden.` : ''}`,
      }]);
      toast.success('Rule removed');
    } catch (e) {
      toast.error(e.message || 'Failed to remove rule');
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset all guardrails? All flagged issues will become visible again.')) return;
    setResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke('issue-filter-assistant', {
        body: { organization_id: organizationId, action: 'reset' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPolicy(data.policy);
      setMessages([]);
      toast.success('Guardrails reset to default');
    } catch (e) {
      toast.error(e.message || 'Failed to reset');
    }
    setResetting(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className={`h-7 w-48 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className={`${cardClass} h-32`} />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-5" : "space-y-6 max-w-4xl"}>
      {!embedded && (
        <>
          <button onClick={() => navigate('/settings')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
            <ArrowLeft className="h-4 w-4" /> Back to Settings
          </button>

          <div>
            <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              Issue Guardrails
            </h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              Talk to the assistant to refine what counts as a flagged issue for your organisation. Your rules are applied by AI: every flagged issue — existing and future — is judged against your intent, and matching issues are hidden across the app (you can always reveal them).
            </p>
          </div>
        </>
      )}

      {/* Current policy summary */}
      <div className={cardClass}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-4 w-4 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
              Active policy
            </span>
          </div>
          <button
            onClick={resetAll}
            disabled={resetting}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'}`}
          >
            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            Reset to default
          </button>
        </div>
        <div className="p-5 space-y-3">
          {/* Always show the summary — it is derived from the full rule list, so
              it describes the whole policy currently in force, not the last edit. */}
          <p className={`text-sm ${isDark ? 'text-[#e4e4e7]' : 'text-[#333]'}`}>
            {policy?.summary || 'Default TutorTracer filtering: show all flagged issues.'}
          </p>
          {policy?.suppressions?.length > 0 ? (
            <div className="space-y-2">
              {policy.suppressions.map((rule, i) => (
                <div key={rule.id || i} className={`flex items-start justify-between gap-3 p-3 rounded-md ${isDark ? 'bg-[#18181b]' : 'bg-[#f9f9f9]'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-[#e5e5e5] text-[#666]'}`}>
                        {(rule.category || 'any').replace(/_/g, ' ')}
                      </span>
                      {/* A rule with no severity cap must not read as "severity <= N". */}
                      <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        {Number(rule.max_severity) >= 10 || rule.max_severity == null
                          ? 'any severity'
                          : `severity ≤ ${rule.max_severity}`}
                      </span>
                    </div>
                    {rule.source_text && (
                      <div className={`text-xs italic mb-1 ${isDark ? 'text-[#71717a]' : 'text-[#888]'}`}>“{rule.source_text}”</div>
                    )}
                    <div className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{rule.reason}</div>
                  </div>
                  <button
                    onClick={() => removeRule(i)}
                    title="Remove rule"
                    className={`p-1.5 rounded transition-colors ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#27272a]' : 'text-[#999] hover:text-red-500 hover:bg-[#eee]'}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No suppression rules. All flagged issues are visible.</p>
          )}
          {/* Legacy free-text policies stored instructions with no structured rule.
              Those are no longer applied — say so instead of hiding them. */}
          {!policy?.suppressions?.length && policy?.instructions?.trim() && (
            <div className={`text-xs rounded-md p-3 ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f9f9f9] text-[#666]'}`}>
              <span className="font-medium">Not in force:</span> this organisation has older free-text guardrails that were never
              converted into rules, so nothing is being hidden by them. Re-send them below to turn them into visible rules.
              <div className={`mt-1 whitespace-pre-wrap ${isDark ? 'text-[#71717a]' : 'text-[#888]'}`}>{policy.instructions}</div>
            </div>
          )}
        </div>
      </div>

      {/* Chat */}
      <div className={cardClass}>
        <div className={`px-5 py-3 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Refine guardrails</span>
        </div>

        <div ref={scrollRef} className="px-5 py-4 max-h-[420px] overflow-y-auto space-y-4">
          {messages.length === 0 ? (
            <div className="py-2">
              <p className={`text-xs mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                Try one of these to start:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {EXAMPLES.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => send(q)}
                    className={`p-3 text-left text-xs rounded-lg border transition-colors ${
                      isDark ? 'border-[#27272a] hover:border-[#3f3f46] text-[#a1a1aa] hover:text-white' : 'border-[#e5e5e5] hover:border-[#d0d0d0] text-[#666] hover:text-[#1a1a1a]'
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? (isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]') : 'accent-gradient'}`}>
                  {m.role === 'user' ? <User className={`h-3.5 w-3.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`} /> : <Bot className="h-3.5 w-3.5 text-white" />}
                </div>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#f0f0f0] text-[#1a1a1a]'
                    : m.error
                      ? isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700'
                      : isDark ? 'bg-[#0d0d0f] border border-[#27272a] text-[#e4e4e7]' : 'bg-white border border-[#e5e5e5] text-[#333]'
                }`}>
                  {m.content}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg accent-gradient flex items-center justify-center">
                <Bot className="h-3.5 w-3.5 text-white" />
              </div>
              <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-[#0d0d0f] border border-[#27272a]' : 'bg-white border border-[#e5e5e5]'}`}>
                <div className="flex gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888]'}`} style={{ animationDelay: '0ms' }} />
                  <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888]'}`} style={{ animationDelay: '120ms' }} />
                  <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${isDark ? 'bg-[#52525b]' : 'bg-[#888]'}`} style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`p-4 border-t flex gap-2 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="e.g. Don't flag standalone swearing unless directed at a student"
            disabled={sending}
            className={`flex-1 min-h-[44px] max-h-[120px] resize-none ${isDark ? 'bg-[#0d0d0f] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || sending}
            className={`px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-40 ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default IssueGuardrails;
